import React, { useState, useEffect, useCallback } from 'react';
import './QuickStats.css';
import { Icons } from './AccountIcons';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';

const QuickStats = ({ userId, todayDisplay, monthDisplay }) => {
  const [statsToday, setStatsToday] = useState({ quizCount: 0, flashcardsStudied: 0, timeSpent: 0 });
  const [statsMonth, setStatsMonth] = useState({ quizCount: 0, flashcardsStudied: 0, timeSpent: 0 });
  const [statsTotal, setStatsTotal] = useState({ quizCount: 0, flashcardsStudied: 0, timeSpent: 0 });
  const [streaks, setStreaks] = useState({ currentStreak: 0, maxStreak: 0 });

  const formatDate = (date) => date.toISOString().split('T')[0];

  const calculateStatsAndStreaks = useCallback((activityDaily) => {
    console.log('Calcul des stats avec activityDaily:', activityDaily);

    const now = new Date();
    const todayStr = formatDate(now);
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let todayStats = { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
    let monthStats = { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
    let totalStats = { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };

    Object.keys(activityDaily || {}).forEach((date) => {
      const parsedDate = new Date(date);
      const dailyData = activityDaily[date];

      // Standardisation : gérer quizCount/quizzes et flashcardsStudied/flashcards
      const quizCount = dailyData.quizCount || dailyData.quizzes || 0;
      const flashcardsStudied = dailyData.flashcardsStudied || dailyData.flashcards || 0;

      totalStats.quizCount += quizCount;
      totalStats.flashcardsStudied += flashcardsStudied;
      totalStats.timeSpent += dailyData.timeSpent || 0;

      if (date === todayStr) {
        todayStats.quizCount += quizCount;
        todayStats.flashcardsStudied += flashcardsStudied;
        todayStats.timeSpent += dailyData.timeSpent || 0;
      }

      if (parsedDate.getMonth() === currentMonth && parsedDate.getFullYear() === currentYear) {
        monthStats.quizCount += quizCount;
        monthStats.flashcardsStudied += flashcardsStudied;
        monthStats.timeSpent += dailyData.timeSpent || 0;
      }
    });

    const dates = Object.keys(activityDaily || {}).sort();
    let currentStreak = 0;
    let maxStreak = 0;
    let currentRun = 0;
    let previousDate = null;

    const today = new Date(todayStr);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayStr = formatDate(yesterday);

    dates.forEach((date) => {
      const currentDate = new Date(date);

      if (previousDate) {
        const diffDays = Math.round((currentDate - previousDate) / (1000 * 60 * 60 * 24));
        if (diffDays === 1) {
          currentRun += 1;
        } else if (diffDays > 1) {
          currentRun = 1;
        }
        maxStreak = Math.max(maxStreak, currentRun);
      } else {
        currentRun = 1;
      }
      previousDate = currentDate;

      if (date === todayStr) {
        currentStreak = currentRun;
      } else if (date === yesterdayStr && !dates.includes(todayStr)) {
        currentStreak = currentRun;
      }
    });

    if (!dates.includes(todayStr) && !dates.includes(yesterdayStr)) {
      currentStreak = 0;
    }

    console.log('Stats calculées - Today:', todayStats);
    console.log('Stats calculées - Month:', monthStats);
    console.log('Stats calculées - Total:', totalStats);
    console.log('Streaks calculées:', { currentStreak, maxStreak });

    setStatsToday(todayStats);
    setStatsMonth(monthStats);
    setStatsTotal(totalStats);
    setStreaks({ currentStreak, maxStreak });
  }, []); // Pas de dépendances dynamiques

  useEffect(() => {
    console.log('QuickStats - userId reçu:', userId);
    if (!userId) {
      console.log('Aucun userId fourni, stats restent à zéro');
      return;
    }

    const loadData = async () => {
      const cachedData = localStorage.getItem(`learnData_${userId}`);
      if (cachedData) {
        const { userData } = JSON.parse(cachedData);
        console.log('Données trouvées dans localStorage:', userData);
        if (userData?.activity?.daily) {
          calculateStatsAndStreaks(userData.activity.daily);
          return;
        }
      }

      try {
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          console.log('Données chargées depuis Firestore:', data);
          calculateStatsAndStreaks(data.activity?.daily || {});
        } else {
          console.log('Aucun document trouvé dans Firestore, stats à zéro');
          calculateStatsAndStreaks({});
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données Firestore:', error);
        calculateStatsAndStreaks({});
      }
    };

    loadData();
  }, [userId, calculateStatsAndStreaks]); // Ajout de calculateStatsAndStreaks comme dépendance

  return (
    <div className="quick-stats-container">
      <div className="quick-stats-header">
        <h2>Statistiques Rapides</h2>
        <p className="quick-stats-subtitle">Suivez votre progression en un coup d'œil</p>
      </div>
      <div className="quick-stats-summary">
        <div className="quick-summary-card">
          <div className="quick-summary-icon">
            <Icons.Calendar />
          </div>
          <div className="quick-summary-content">
            <h3>Aujourd'hui</h3>
            <div className="quick-summary-stats">
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsToday.quizCount}</span>
                <span className="quick-stat-label">Quiz</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsToday.flashcardsStudied}</span>
                <span className="quick-stat-label">Flashcards</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsToday.timeSpent}</span>
                <span className="quick-stat-label">Minutes</span>
              </div>
            </div>
            <div className="quick-summary-date">{todayDisplay}</div>
          </div>
        </div>
        <div className="quick-summary-card">
          <div className="quick-summary-icon">
            <Icons.ChartPie />
          </div>
          <div className="quick-summary-content">
            <h3>Ce Mois</h3>
            <div className="quick-summary-stats">
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsMonth.quizCount}</span>
                <span className="quick-stat-label">Quiz</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsMonth.flashcardsStudied}</span>
                <span className="quick-stat-label">Flashcards</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsMonth.timeSpent}</span>
                <span className="quick-stat-label">Minutes</span>
              </div>
            </div>
            <div className="quick-summary-date">{monthDisplay}</div>
          </div>
        </div>
        <div className="quick-summary-card">
          <div className="quick-summary-icon">
            <Icons.BookOpen />
          </div>
          <div className="quick-summary-content">
            <h3>Total</h3>
            <div className="quick-summary-stats">
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsTotal.quizCount}</span>
                <span className="quick-stat-label">Quiz</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsTotal.flashcardsStudied}</span>
                <span className="quick-stat-label">Flashcards</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-value">{statsTotal.timeSpent}</span>
                <span className="quick-stat-label">Minutes</span>
              </div>
            </div>
          </div>
        </div>
        <div className="quick-summary-card">
          <div className="quick-summary-icon">
            <Icons.Trophy />
          </div>
          <div className="quick-summary-content">
            <h3>Série</h3>
            <div className="quick-streak-stats">
              <div className="quick-streak-current">
                <span className="quick-streak-value">{streaks.currentStreak}</span>
                <span className="quick-streak-label">Actuelle</span>
              </div>
              <div className="quick-streak-divider"></div>
              <div className="quick-streak-max">
                <span className="quick-streak-value">{streaks.maxStreak}</span>
                <span className="quick-streak-label">Record</span>
              </div>
            </div>
            <div className="quick-streak-unit">jours</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStats;